import { Link as RouterLink, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { dec } from '@praticheenergia/ad1-engine'
import {
  Alert, Box, Link, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material'
import { elencaImpiantiGreen, serieImpianto } from './green.api'
import { MESI, etichettaMese, kwh } from './formato'
import { ChipStato } from './Monitoraggio'
import { InArrivo } from '../layout/Pagina'

export function DettaglioGreen() {
  const { idPartner, idGreen } = useParams()
  const id = Number(idGreen)
  const { data: impianti = [] } = useQuery({ queryKey: ['impianti-green'], queryFn: elencaImpiantiGreen })
  const { data: serie = [], error } = useQuery({ queryKey: ['serie-green', id], queryFn: () => serieImpianto(id) })
  const impianto = impianti.find((i) => i.id === id)

  const conDati = serie.filter((m) => m.produzione !== null)
  const anno = conDati.at(-1)?.anno ?? new Date().getFullYear()
  const produzione = (a: number, m: number) => serie.find((s) => s.anno === a && s.mese === m)?.produzione ?? null
  const massimo = conDati.reduce((t, m) => (dec(m.produzione!).greaterThan(t) ? dec(m.produzione!) : t), dec(0))
  const altezza = (v: string | null) => (v === null || massimo.isZero() ? 0 : Number(dec(v).div(massimo).times(100).toFixed(1)))

  return (
    <Stack spacing={2.5}>
      <Link component={RouterLink} to={`/cliente/${idPartner}/impianti-green`}>← Monitoraggio</Link>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5" component="h1">{impianto?.ragioneSociale ?? 'Impianto'}</Typography>
          <Typography color="text.secondary">
            {[impianto?.codiceImpianto, impianto?.comune, impianto?.kwp && `${impianto.kwp} kWp`, impianto?.gestoreRete].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        {impianto && (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="body2">Distributore</Typography><ChipStato stato={impianto.statoDistributore} />
            <Typography variant="body2">GSE</Typography><ChipStato stato={impianto.statoGse} />
          </Stack>
        )}
      </Stack>

      {error && <Alert severity="error">{error.message}</Alert>}

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">Produzione {anno} contro {anno - 1}</Typography>
          <Stack direction="row" spacing={2} sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              <Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: 'primary.main' }} /> {anno}
            </Box>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              <Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: '#bfd0f7' }} /> {anno - 1}
            </Box>
          </Stack>
        </Stack>
        <Box role="img" aria-label={`Produzione mensile ${anno} confrontata con ${anno - 1}`}
          sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: { xs: 0.5, sm: 1.5 }, alignItems: 'end', height: 200 }}>
          {MESI.map((nome, i) => {
            const attuale = produzione(anno, i + 1)
            const precedente = produzione(anno - 1, i + 1)
            return (
              <Box key={nome} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                <Box sx={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '2px' }}>
                  <Box title={`${etichettaMese(anno - 1, i + 1)}: ${kwh(precedente)}`} sx={{ width: '40%', height: `${altezza(precedente)}%`, bgcolor: '#bfd0f7', borderRadius: '3px 3px 0 0' }} />
                  <Box title={`${etichettaMese(anno, i + 1)}: ${kwh(attuale)}`} sx={{ width: '40%', height: `${altezza(attuale)}%`, bgcolor: 'primary.main', borderRadius: '3px 3px 0 0' }} />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>{nome}</Typography>
              </Box>
            )
          })}
        </Box>
      </Paper>

      <Paper variant="outlined">
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2 }}>
          <Typography variant="h6" component="h2">Dati mensili</Typography>
          <InArrivo etichetta="Importa nella dichiarazione" />
        </Stack>
        <TableContainer>
          <Table size="small" aria-label="Dati mensili">
            <TableHead>
              <TableRow>
                <TableCell>Mese</TableCell>
                <TableCell align="right">Produzione</TableCell>
                <TableCell align="right">Immissione</TableCell>
                <TableCell align="right">Autoconsumo</TableCell>
                <TableCell align="right">Prelievo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...serie].reverse().slice(0, 12).map((m) => (
                <TableRow key={`${m.anno}-${m.mese}`}>
                  <TableCell>{etichettaMese(m.anno, m.mese)}</TableCell>
                  <TableCell align="right">{kwh(m.produzione)}</TableCell>
                  <TableCell align="right">{kwh(m.immissione)}</TableCell>
                  <TableCell align="right">{kwh(m.autoconsumo)}</TableCell>
                  <TableCell align="right">{kwh(m.prelievo)}</TableCell>
                </TableRow>
              ))}
              {serie.length === 0 && (
                <TableRow><TableCell colSpan={5}><Typography color="text.secondary" sx={{ py: 2 }}>Nessun dato scaricato per questo impianto.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  )
}
